import React, { useState, useEffect } from 'react';
import { Box, styled } from '@mui/material';

const Cursor = styled('span')<{ $isComplete?: boolean }>(({ theme, $isComplete }) => ({
  display: 'inline-block',
  visibility: $isComplete ? 'hidden' : 'visible',
  width: '2px',
  height: '1.2em',
  backgroundColor: theme.palette.text.primary,
  marginLeft: '2px',
  verticalAlign: 'middle',
  transform: 'translateY(-10%)',
  animation: 'blink 1s step-end infinite',
  '@keyframes blink': {
    'from, to': { opacity: 1 },
    '50%': { opacity: 0 },
  },
}));

const StyledText = styled('span')<{ $showUnderline?: boolean; $isComplete?: boolean }>(
  ({ $showUnderline, $isComplete }) => ({
    fontWeight: $isComplete ? 700 : 'inherit',
    borderBottom: $showUnderline ? '2px solid currentColor' : 'none',
    paddingBottom: $showUnderline ? '2px' : 0,
  })
);

interface AnimatedTypewriterProps {
  words: string[];
  typingSpeed?: number;
  erasingSpeed?: number;
  delayBetweenWords?: number;
  className?: string;
  loop?: boolean;
  showUnderline?: boolean;
}

export const AnimatedTypewriter: React.FC<AnimatedTypewriterProps> = ({
  words,
  typingSpeed = 150,
  erasingSpeed = 100,
  delayBetweenWords = 2000,
  className,
  loop = true,
  showUnderline = false,
}) => {
  const [currentText, setCurrentText] = useState('');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!words.length || isComplete) return;

    const currentWord = words[currentWordIndex];
    const isLastWord = currentWordIndex === words.length - 1;

    if (isPaused) {
      const pauseTimeout = setTimeout(() => {
        // If it's the last word and we're not looping, stay on the final word
        if (isLastWord && !loop) {
          setIsComplete(true);
          return;
        }
        setIsPaused(false);
        setIsTyping(false);
      }, delayBetweenWords);
      return () => clearTimeout(pauseTimeout);
    }

    if (isTyping) {
      if (currentText === currentWord) {
        setIsPaused(true);
        return;
      }

      const typingTimeout = setTimeout(() => {
        setCurrentText(currentWord.slice(0, currentText.length + 1));
      }, typingSpeed);
      return () => clearTimeout(typingTimeout);
    } else {
      if (currentText === '') {
        if (isLastWord && !loop) {
          setCurrentWordIndex(0);
          setIsTyping(true);
        } else {
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
          setIsTyping(true);
        }
        return;
      }

      const erasingTimeout = setTimeout(() => {
        setCurrentText(currentText.slice(0, -1));
      }, erasingSpeed);
      return () => clearTimeout(erasingTimeout);
    }
  }, [currentText, currentWordIndex, isTyping, isPaused, isComplete, words, typingSpeed, erasingSpeed, delayBetweenWords, loop]);

  return (
    <Box component="span" className={className}>
      <StyledText $showUnderline={showUnderline} $isComplete={isComplete}>
        {currentText}
      </StyledText>
      <Cursor $isComplete={isComplete} />
    </Box>
  );
}; 